/**
 * Overview — account-home page. CF-style green.
 *   - Header (no gradient; simple ink text)
 *   - 4 metric tiles with sparkline + delta
 *   - 2 section cards (Top campaigns, Partners)
 *
 * Numbers here are hardcoded MVP examples; wire to real APIs later.
 */
import { Sparkline } from "../components/app-shell/Sparkline";
import {
	ArrowDown,
	ArrowUp,
	BarChart,
	Check,
	CreditCard,
	Megaphone,
} from "../components/app-shell/icons";

type Metric = {
	label: string;
	value: string;
	deltaPct: number; // positive = up
	icon: "Megaphone" | "Check" | "CreditCard" | "BarChart";
	points: number[];
};

const METRICS: Metric[] = [
	{
		label: "Clicks",
		value: "12,340",
		deltaPct: 12.4,
		icon: "Megaphone",
		points: [8, 10, 9, 11, 13, 12, 14, 15, 17, 16, 18, 20],
	},
	{
		label: "Conversions",
		value: "428",
		deltaPct: 5.1,
		icon: "Check",
		points: [3, 4, 3, 5, 4, 6, 5, 7, 6, 7, 8, 7],
	},
	{
		label: "Revenue",
		value: "$4,210",
		deltaPct: 18.7,
		icon: "CreditCard",
		points: [2, 3, 4, 3, 5, 6, 5, 7, 8, 9, 11, 13],
	},
	{
		label: "Active campaigns",
		value: "17",
		deltaPct: -2.3,
		icon: "BarChart",
		points: [19, 19, 18, 19, 17, 18, 17, 18, 17, 17, 16, 17],
	},
];

const ICON_FOR = {
	Megaphone,
	Check,
	CreditCard,
	BarChart,
} as const;

const TOP_CAMPAIGNS = [
	{ name: "Summer launch US", status: "active", rev: 1820 },
	{ name: "iOS lifestyle AU", status: "active", rev: 1340 },
	{ name: "Winter VPN EU", status: "paused", rev: 512 },
	{ name: "Casino reactivation", status: "active", rev: 340 },
	{ name: "AdSpark cashback UK", status: "active", rev: 198 },
];

const PARTNER_ROWS = [
	{ label: "Active affiliates", value: "124" },
	{ label: "Pending review", value: "7" },
	{ label: "Top affiliate network", value: "AdSpark" },
];

function MetricTile({ m }: { m: Metric }) {
	const positive = m.deltaPct >= 0;
	const Icon = ICON_FOR[m.icon];
	const Arrow = positive ? ArrowUp : ArrowDown;
	const deltaColor = positive ? "text-green-700" : "text-red-700";
	return (
		<div className="cf-card p-5">
			<div className="flex items-center justify-between">
				<span className="cf-section-header">{m.label}</span>
				<Icon className="w-4 h-4 text-zinc-400" />
			</div>
			<div className="cf-metric-num mt-2">{m.value}</div>
			<div className="flex items-center gap-1 mt-1">
				<Arrow className={`w-3 h-3 ${deltaColor}`} />
				<span className={`text-xs ${deltaColor}`}>
					{positive ? "+" : ""}
					{m.deltaPct.toFixed(1)}%
				</span>
				<span className="text-xs text-zinc-400 ml-2">vs prev 7d</span>
			</div>
			<div className="mt-3">
				<Sparkline points={m.points} positive={positive} />
			</div>
		</div>
	);
}

export function IndexRoute() {
	return (
		<div>
			<header>
				<h1 className="text-2xl font-semibold text-zinc-900">Overview</h1>
				<p className="text-sm text-zinc-500 mt-1">
					appsclicksnh@gmail.com's Account
				</p>
			</header>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
				{METRICS.map((m) => (
					<MetricTile key={m.label} m={m} />
				))}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
				<section className="cf-card p-5">
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-zinc-900">
							Top campaigns
						</h2>
						<a
							href="/campaign-dashboard"
							className="text-xs font-medium text-green-700 hover:text-green-800"
						>
							View all
						</a>
					</div>
					<ul className="mt-4 flex flex-col divide-y divide-zinc-100">
						{TOP_CAMPAIGNS.map((c) => (
							<li
								key={c.name}
								className="flex items-center justify-between py-2.5"
							>
								<div className="flex items-center gap-2 min-w-0">
									<span
										className={
											c.status === "active"
												? "w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"
												: "w-1.5 h-1.5 rounded-full bg-zinc-400 shrink-0"
										}
									/>
									<span className="text-[13px] text-zinc-900 truncate">
										{c.name}
									</span>
								</div>
								<span className="text-[13px] text-zinc-600 tabular-nums">
									${c.rev.toLocaleString()}
								</span>
							</li>
						))}
					</ul>
				</section>

				<section className="cf-card p-5">
					<div className="flex items-center justify-between">
						<h2 className="text-sm font-semibold text-zinc-900">Partners</h2>
						<a
							href="/affiliates/groups"
							className="text-xs font-medium text-green-700 hover:text-green-800"
						>
							View all
						</a>
					</div>
					<ul className="mt-4 flex flex-col divide-y divide-zinc-100">
						{PARTNER_ROWS.map((r) => (
							<li
								key={r.label}
								className="flex items-center justify-between py-2.5"
							>
								<span className="text-[13px] text-zinc-600">{r.label}</span>
								<span className="text-[13px] text-zinc-900 font-medium">
									{r.value}
								</span>
							</li>
						))}
					</ul>
				</section>
			</div>
		</div>
	);
}
